import { useEffect, useRef } from 'react'
import { usePresence } from '@/contexts/PresenceContext'
import { useUser } from '@/app/store/auth'
import { useChatNotificationStore } from '@/app/store/chatNotifications'
import {
    initializeAudio,
    playUserChatNotification,
} from '@/features/ai-chat/core/utils/notificationSounds'
import { isChatAppMounted } from '@/features/ai-chat/core/utils/chatNotificationState'
import api from '@/lib/api/axios'

interface UnreadUpdate {
    sessionId: string | number
    unreadCount: number
    lastMessageTimestamp?: string | number
}

/**
 * App-wide chat notifications.
 *
 * The chat backend emits `unread-update` to the per-user socket room, which the
 * always-connected PresenceContext socket already belongs to. This component
 * keeps a running total of unseen messages (seeded from the unread API, then
 * updated live) and exposes it via the store so the taskbar AI Assistant icon
 * shows a "messages waiting" badge — wherever the user is in the app. It also
 * plays the notification sound when a new message arrives while the chat UI
 * is closed (the chat UI owns its own sound while open).
 */
export function GlobalChatNotifications() {
    const { socket } = usePresence()
    const user = useUser()
    // Per-session unread counts; summed into the store total.
    const countsRef = useRef<Record<string, number>>({})
    const seededRef = useRef(false)

    const syncTotal = () => {
        const total = Object.values(countsRef.current).reduce((a, b) => a + b, 0)
        useChatNotificationStore.getState().setUnreadCount(total)
    }

    // Browsers require a user gesture before audio can play. Prime the audio
    // context on the first interaction anywhere in the app.
    useEffect(() => {
        const prime = () => {
            initializeAudio()
            document.removeEventListener('click', prime, true)
            document.removeEventListener('keydown', prime, true)
            document.removeEventListener('touchstart', prime, true)
        }
        document.addEventListener('click', prime, true)
        document.addEventListener('keydown', prime, true)
        document.addEventListener('touchstart', prime, true)
        return () => {
            document.removeEventListener('click', prime, true)
            document.removeEventListener('keydown', prime, true)
            document.removeEventListener('touchstart', prime, true)
        }
    }, [])

    // Seed the badge with current unread counts on load (no socket event fires
    // for messages received while the user was away).
    useEffect(() => {
        if (!user?.id || seededRef.current) return
        seededRef.current = true
        api.get('/api/ai/user-chat/unread-counts')
            .then((r) => r.data)
            .then((d) => {
                if (!d) return
                for (const item of d.unreadCounts || []) {
                    if (item.sessionId) countsRef.current[String(item.sessionId)] = item.unreadCount || 0
                }
                syncTotal()
            })
            .catch(() => {})
    }, [user?.id])

    useEffect(() => {
        if (!socket) return

        const handleUnread = (data: UnreadUpdate) => {
            const key = String(data.sessionId)
            const prev = countsRef.current[key] || 0
            const next = data.unreadCount || 0
            countsRef.current[key] = next
            syncTotal()

            // Sound only on a genuine increase, and only when the full chat UI
            // isn't already handling its own notifications.
            if (next > prev && !isChatAppMounted()) {
                playUserChatNotification()
            }
        }

        // There is no AI-assistant listener here. A monitoring alert is never persisted and is
        // emitted only into the room of the conversation that raised it, so the window holding
        // the panel is the only one that can know about it: ChatApp counts it and mirrors the
        // count onto the store, which is what lights the taskbar badge.
        socket.on('unread-update', handleUnread)
        return () => {
            socket.off('unread-update', handleUnread)
        }
    }, [socket])

    return null
}
